/**
 * Native shell integration.
 *
 * Every call is a no-op on the web (`Capacitor.isNativePlatform()`), so the same
 * bundle runs in a browser and inside the iOS/Android WebView without branching at
 * the call sites.
 */

import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platform(): string {
  return Capacitor.getPlatform();
}

/**
 * Configure the native chrome once, at boot.
 *
 * @param scheme - Active colour scheme; the status bar text has to contrast with the
 *   app background, so it is inverted relative to the scheme.
 * @param onBack - Handles the Android hardware back button. Returning `false` means
 *   "nothing left to pop", which exits the app.
 */
export async function initializeNativeShell(
  scheme: "light" | "dark",
  onBack: () => boolean,
  onOpenUrl?: (url: string) => void,
): Promise<() => void> {
  if (!isNative()) return () => undefined;

  await Promise.allSettled([
    // `Style.Dark` means "light text for dark backgrounds" and `Style.Light` means
    // "dark text for light backgrounds" — the names describe the bar's style, not the
    // text colour. The scheme therefore maps straight through, not inverted.
    StatusBar.setStyle({ style: scheme === "dark" ? Style.Dark : Style.Light }),
    // Resize the WebView body rather than pushing the whole view up, so a focused
    // input scrolls into place instead of the header sliding off screen.
    Keyboard.setResizeMode({ mode: KeyboardResize.Body }),
    Keyboard.setAccessoryBarVisible({ isVisible: false }),
  ]);

  const backHandle = await App.addListener("backButton", ({ canGoBack }) => {
    const handled = onBack();
    if (!handled && !canGoBack) void App.exitApp();
  });

  // `appUrlOpen` covers a warm app; `getLaunchUrl` covers a process started by the
  // same custom/universal link. Both pass through one callback so routing semantics
  // cannot drift between the two lifecycle paths.
  const urlHandle = await App.addListener("appUrlOpen", ({ url }) => {
    onOpenUrl?.(url);
  });
  const launch = await App.getLaunchUrl().catch(() => undefined);
  if (launch?.url) onOpenUrl?.(launch.url);

  // Hidden only once the first React frame has painted, so the user never sees a
  // white flash between the splash image and the app.
  await SplashScreen.hide();

  return () => {
    void backHandle.remove();
    void urlHandle.remove();
  };
}

/**
 * Force light status-bar glyphs, whatever the colour scheme says.
 *
 * `syncStatusBarStyle` derives the style from the scheme, which is right for every
 * screen whose top edge is a scheme surface. A screen that runs a surface which is
 * dark in *both* schemes under the notch — the welcome screen's arrival band — needs
 * this instead: in light mode the derived style paints a black clock, black battery
 * and black signal bars onto a near-black band. Call `syncStatusBarStyle` on the way
 * out to hand the bar back to the scheme.
 */
export async function forceLightStatusBarGlyphs(): Promise<void> {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
    // A status bar that could not be restyled is cosmetic; never fail a render for it.
  }
}

/** Keep the status bar readable when the user switches light/dark at runtime. */
export async function syncStatusBarStyle(
  scheme: "light" | "dark",
): Promise<void> {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({
      style: scheme === "dark" ? Style.Dark : Style.Light,
    });
  } catch {
    // Not fatal — the bar simply keeps its previous style.
  }
}

/**
 * A light tap under the finger, for an action that completes something — ticking a
 * dose off. Fire-and-forget: a phone without a haptic engine simply stays still.
 */
export function tapFeedback(): void {
  if (!isNative()) return;
  void Haptics.impact({ style: ImpactStyle.Light }).catch(() => undefined);
}

/**
 * Run `callback` every time the app returns to the foreground, until the returned
 * function is called. Native listens for Capacitor's `resume`; the web falls back to
 * the tab becoming visible again, like `onNextResume` below.
 */
export function onResume(callback: () => void): () => void {
  if (isNative()) {
    const pending = App.addListener("resume", callback);
    return () => void pending.then((handle) => handle.remove());
  }
  const handle = () => {
    if (document.visibilityState === "visible") callback();
  };
  document.addEventListener("visibilitychange", handle);
  return () => document.removeEventListener("visibilitychange", handle);
}

/**
 * Run `callback` once, the next time the app returns to the foreground.
 *
 * Used after handing off to an external browser (the storefront): there is no
 * callback URL, so coming back to the app is the one signal that the member may
 * have finished. Native listens for Capacitor's `resume`; the web falls back to
 * the tab becoming visible again.
 */
export function onNextResume(callback: () => void): void {
  if (isNative()) {
    // Remove only this handle — `backButton` and friends must keep listening.
    const pending = App.addListener("resume", () => {
      void pending.then((handle) => handle.remove());
      callback();
    });
    return;
  }
  const handle = () => {
    if (document.visibilityState !== "visible") return;
    document.removeEventListener("visibilitychange", handle);
    callback();
  };
  document.addEventListener("visibilitychange", handle);
}
